import { PincodeRepository } from "../../domain/interfaces/PincodeRepository";
import { PincodeDetails } from "../../domain/models/PincodeDetails";
import {
  resolveInitialPincode,
  type ResolveInitialPincodeInput,
} from "../../domain/resolveInitialPincode";
import type { StorageRepository } from "@/core/services/storage/domain/interfaces/StorageRepository";
import type { PhysicalStoreRepository } from "@/shared/features/physical-store/domain/interfaces/PhysicalStoreRepository";

type PincodeContextStored = {
  pincode?: unknown;
  hasTAH?: unknown;
  hasStores?: unknown;
  sid?: unknown;
  wid?: unknown;
  message?: unknown;
};

export function PincodeService(
  repo: PincodeRepository,
  storage: StorageRepository,
  physicalStoreRepo?: PhysicalStoreRepository
) {
  return {
    resolveInitialPincode(input: ResolveInitialPincodeInput): string | null {
      return resolveInitialPincode(input);
    },

    isPincodeUpdated(): boolean {
      const isPincodeUpdated = storage.localGetData<boolean | string>("isPincodeUpdated");
      return (
        isPincodeUpdated === true ||
        (typeof isPincodeUpdated === "string" &&
          isPincodeUpdated.trim().toLowerCase() === "true")
      );
    },

    async updatePincodeContext(pincode: string): Promise<PincodeDetails | null> {
      const details = await repo.fetchPincodeDetails(pincode);
      if (!details) return null;

      const expiryDays = 1;

      storage.localSaveData("pincode", pincode, { expiryDays });
      storage.localSaveData("hasTAH", details.hasTAH ?? false, { expiryDays });
      storage.localSaveData("hasStores", details.hasStores ?? false, { expiryDays });
      storage.localSaveData("storeIds", details.sid ?? "", { expiryDays });
      storage.localSaveData("tahHubId", details.wid ?? "", { expiryDays });
      storage.localSaveData("isPincodeUpdated", true, { expiryDays });
      storage.localSaveData(
        "pincodeContext",
        {
          pincode,
          hasTAH: details.hasTAH ?? false,
          hasStores: details.hasStores ?? false,
          sid: details.sid ?? "",
          wid: details.wid ?? "",
          message: details.message ?? "",
        },
        { expiryDays }
      );

      physicalStoreRepo?.invalidateStoreLocationWidget();

      return details;
    },

    getCachedPincode(): string | null {
      const isUpdated = this.isPincodeUpdated();

      if (isUpdated) {
        const p = storage.localGetData<string>("pincode");
        if (typeof p === "string" && p.trim()) return p.trim();
      }

      const value = storage.localGetData<string>("pincode");
      return typeof value === "string" && value.trim() ? value.trim() : null;
    },

    getCachedPincodeContext(): {
      hasTAH: boolean;
      hasStores: boolean;
      sid: string;
      wid: string;
      message: string;
    } | null {
      const raw = storage.localGetData<PincodeContextStored>("pincodeContext");
      if (!raw || typeof raw !== "object") return null;
      if (typeof raw.pincode !== "string" || !raw.pincode.trim()) return null;

      return {
        hasTAH: Boolean(raw.hasTAH),
        hasStores: Boolean(raw.hasStores),
        sid: typeof raw.sid === "string" ? raw.sid : "",
        wid: typeof raw.wid === "string" ? raw.wid : "",
        message: typeof raw.message === "string" ? raw.message : "",
      };
    },
  };
}
